import type { TFunction } from '@unifyapps/i18n/client';
import { formatDuration } from 'date-fns';
import findLastIndex from 'lodash/findLastIndex';

const MILLISECONDS = 1,
  SECOND = 1000,
  MINUTE = 60 * SECOND,
  HOUR = 60 * MINUTE,
  DAY = 24 * HOUR,
  MONTH = 31.5 * DAY,
  YEAR = 365 * DAY;

const millisPreMul = [YEAR, MONTH, DAY, HOUR, MINUTE, SECOND, MILLISECONDS];
const precisionKeys = [
  'years',
  'months',
  'days',
  'hours',
  'minutes',
  'seconds',
  'milliseconds',
] as const;
const compactDurationSuffixes = ['y', 'mo', 'd', 'h', 'm', 's', 'ms'];

/**
 * Formats a time in milliseconds to a string.
 * @param ms - The time in milliseconds
 * @param precision - The precision of the time. Can be 'years', 'months', 'days', 'hours', 'minutes', 'seconds', or 'milliseconds'. Shows units up to the specified precision.
 * @returns The formatted time
 *
 * @example
 * ```tsx
 * formatTime(1005, 'hours'); // '0h'
 * formatTime(1005, 'seconds'); // '1s'
 * formatTime(1005, 'milliseconds'); // '1s 5ms'
 * formatTime(3610001, 'seconds'); // '1h 0m 10s'
 * formatTime(3500000, 'hours'); // '0h'
 * formatTime(3600000, 'hours'); // '1h'
 * ```
 */

function translateFormattedTime(formattedTime: string, t: TFunction): string {
  let translatedTime = formattedTime.trim();

  // Define patterns for matching time units
  // Order matters - longer patterns first to avoid partial matches
  const patterns = [
    // Full words first
    { pattern: /\bmilliseconds\b/g, key: 'milliseconds' },
    { pattern: /\bmillisecond\b/g, key: 'millisecond' },
    { pattern: /\bseconds\b/g, key: 'seconds' },
    { pattern: /\bsecond\b/g, key: 'second' },
    { pattern: /\bminutes\b/g, key: 'minutes' },
    { pattern: /\bminute\b/g, key: 'minute' },
    { pattern: /\bhours\b/g, key: 'hours' },
    { pattern: /\bhour\b/g, key: 'hour' },
    { pattern: /\bdays\b/g, key: 'days' },
    { pattern: /\bday\b/g, key: 'day' },
    { pattern: /\bmonths\b/g, key: 'months' },
    { pattern: /\bmonth\b/g, key: 'month' },
    { pattern: /\byears\b/g, key: 'years' },
    { pattern: /\byear\b/g, key: 'year' },

    // More specific compact patterns (number followed by unit)
    { pattern: /(\d+)\s*mo\b/g, key: 'mo', numbered: true },
    { pattern: /(\d+)\s*ms\b/g, key: 'ms', numbered: true },
    { pattern: /(\d+)\s*y\b/g, key: 'y', numbered: true },
    { pattern: /(\d+)\s*d\b/g, key: 'd', numbered: true },
    { pattern: /(\d+)\s*h\b/g, key: 'h', numbered: true },
    { pattern: /(\d+)\s*m\b/g, key: 'm', numbered: true },
    { pattern: /(\d+)\s*s\b/g, key: 's', numbered: true },
  ];

  patterns.forEach(({ pattern, key, numbered }) => {
    const translation = t(`common:Time.${key}`);
    if (translation?.trim()) {
      if (numbered) {
        // For numbered patterns, preserve the number
        translatedTime = translatedTime.replace(pattern, `$1${translation}`);
      } else {
        translatedTime = translatedTime.replace(pattern, translation);
      }
    }
  });

  return translatedTime;
}

const millisInPreviousDurations = (durationArray: number[]) =>
  durationArray.reduce((acc, _, i) => acc + durationArray[i] * millisPreMul[i], 0);

/*
 * WARNING: This function is exposed in the no-code platform utilities.
 * Any API changes must maintain backward compatibility.
 * Documentation updates must be made in packages/carbon/docs/website/utilities.md
 * before deploying breaking changes.
 */
export function formatTime(
  ms: number,
  precision:
    | 'years'
    | 'months'
    | 'days'
    | 'hours'
    | 'minutes'
    | 'seconds'
    | 'milliseconds' = 'milliseconds',
  notation: 'comfortable' | 'compact' = 'compact',
  maxSignificantUnits = millisPreMul.length,
  t?: TFunction,
) {
  // EXAMPLE ms 269329401003 = ( 3 * MILLISECONDS  +  21 * SECOND  +  43 * MINUTE  +  5 * HOUR  +  8 * DAY  +  6 * MONTHS  +  8 * YEAR )

  /**
   * Calculate duration array for given milliseconds where
   * [index 0 is years, index 1 is months, index 2 is days, index 3 is hours, index 4 is minutes, index 5 is seconds, index 6 is milliseconds]
   */
  const durationArray = millisPreMul.reduce<number[]>((acc, _, index) => {
    const msInPreviousCalculatedDurations = index > 0 ? millisInPreviousDurations(acc) : 0;

    return [...acc, Math.floor((ms - msInPreviousCalculatedDurations) / millisPreMul[index])];
  }, []);
  // EXAMPLE ANS: duration array [8, 6, 8, 5, 43, 21, 3]

  /**
   * endIndex - index of above duration array until which duration should be displayed
   * it should be according to precision
   * minimum is to ensure that duration value of that index is not zero
   */

  const firstNonZeroIndex = durationArray.findIndex((val) => val > 0);
  const lastNonZeroIndex = findLastIndex(durationArray, (val) => val > 0);

  const lastIndexAccordingToPrecision = precisionKeys.indexOf(precision);

  const maximumSignificantUnits = Math.min(
    Math.max(maxSignificantUnits, 1),
    millisPreMul.length - 1,
  );

  const lastSignificantIndex = firstNonZeroIndex + maximumSignificantUnits - 1;

  const endIndex = Math.min(lastIndexAccordingToPrecision, lastSignificantIndex, lastNonZeroIndex);
  // EXAMPLE ANS: 6

  /**
   * startIndex is highest duration unit which has non zero value for given milliseconds
   */
  const startIndex = durationArray.findIndex((val) => val > 0);
  // EXAMPLE ANS: 0

  /**
   * When we want to display duration as 8 years 6 months 8 days 5 hours 43 minutes 21 seconds 3 milliseconds
   */
  if (notation === 'comfortable') {
    /**
     * key value map of above duration array
     */
    const durationObj = precisionKeys.reduce(
      (acc, key, index) => {
        if (index < startIndex || index > endIndex) return acc;

        acc[key] = durationArray[index];

        return acc;
      },
      {} as Partial<{ [key in (typeof precisionKeys)[number]]: number }>,
    );
    // EXAMPLE ANS: { milliseconds: 3, seconds: 21, minutes: 43, hours: 5, days: 8, months: 6, years: 8 }

    //milliseconds not handled by date-fns
    const appendableMilliSecondsString = durationObj.milliseconds
      ? ` ${durationObj.milliseconds} milliseconds`
      : '';

    const formattedDuration = `${formatDuration(durationObj)}${appendableMilliSecondsString}`;

    if (t) {
      return translateFormattedTime(formattedDuration, t);
    }

    return formattedDuration;
    // EXAMPLE RETURN: 8 years 6 months 8 days 5 hours 43 minutes 21 seconds 3 milliseconds
  }

  /**
   * When we want to display duration as 8y 6m 8d 5h 43mi 21s 3ms
   * create a string as above from duration array
   */
  const formattedDuration = compactDurationSuffixes.reduce((acc, compactDurationSuffix, index) => {
    if (index < startIndex || index > endIndex) {
      return acc;
    }

    return `${acc} ${durationArray[index]}${compactDurationSuffix}`;
  }, '');

  if (t) {
    return translateFormattedTime(formattedDuration, t);
  }

  return formattedDuration;
  // EXAMPLE RETURN: 8y 6m 8d 5h 43mi 21s 3ms
}
